'use client';

import { Box, Button, Divider, Menu, MenuItem } from '@mui/material';
import { FC, useRef, useState } from 'react';

import {
  AbgTypography,
  ArrowDownIcon,
  CreditCardIcon,
  MasterCardIcon,
  VisaIcon,
} from '@abg/kit-base';

import { SplitAmount } from '../SplitAmount/SplitAmount';

import {
  userCardsDividerStyles,
  userCardsMenuItemStyles,
  userCardsMenuListStyles,
  userCardsMenuPaperStyles,
  userCardsSelectLabelTypographyStyles,
  userCardsSelectStyles,
  userCardsSelectTypographyStyles,
  userCardsWrapper,
} from './user-card-select.styling';

export type UserCardsSelectProps = {
  label: string;
  value: string;
  options: { cardNumber: string; cardType: string }[];
  onChange: (value: string) => void;
  amount: { primary: string; secondary: string } | undefined;
  title: string;
  primaryCard: string;
  onAddCard?: () => void;
  addCardLabel?: string;
};

export const UserCardsSelect: FC<UserCardsSelectProps> = ({
  label,
  value,
  options,
  onChange,
  amount,
  title,
  primaryCard,
  onAddCard,
  addCardLabel = 'Add a new card',
}) => {
  const [anchorMenu, setAnchorMenu] = useState<
    null | HTMLElement | SVGSVGElement
  >(null);

  const selectCardButtonReference = useRef<HTMLButtonElement | null>(null);
  const selectCardPencilReference = useRef<SVGSVGElement | null>(null);

  const open = Boolean(anchorMenu);

  /**
   * Handles click events for both button and icon elements by setting the anchor menu
   * to the appropriate reference element.
   *
   * @param type - The type of element that was clicked, either 'button' or 'icon'
   *
   * @remarks
   * - If type is 'icon', sets the anchor menu to the pencil icon reference
   * - If type is 'button', sets the anchor menu to the button reference
   */
  const handleClick = (type: 'button' | 'icon') => {
    setAnchorMenu(
      type === 'icon'
        ? selectCardPencilReference.current
        : selectCardButtonReference.current,
    );
  };

  const handleClose = () => {
    setAnchorMenu(null);
  };

  return (
    <>
      <Box sx={userCardsWrapper}>
        <AbgTypography
          variant="body2"
          sx={userCardsSelectLabelTypographyStyles}
          data-testid="user-cards-select-label"
        >
          {label}
        </AbgTypography>

        <Button
          ref={selectCardButtonReference}
          id="demo-positioned-button"
          aria-controls={open ? 'demo-positioned-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={() => handleClick('button')}
          startIcon={<CreditCardIcon />}
          endIcon={<ArrowDownIcon />}
          sx={userCardsSelectStyles}
          data-testid="user-cards-select-button"
        >
          <AbgTypography
            variant="body2"
            sx={userCardsSelectTypographyStyles}
            data-testid="user-cards-select-button-text"
          >
            {value}
          </AbgTypography>
        </Button>

        <Menu
          id="demo-positioned-menu"
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorMenu}
          open={open}
          data-testid="user-cards-select-menu"
          slotProps={{
            paper: { style: userCardsMenuPaperStyles },
            list: {
              sx: userCardsMenuListStyles,
            },
          }}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {options?.map(({ cardNumber, cardType }) => (
            <MenuItem
              key={cardNumber}
              onClick={() => onChange(cardNumber)}
              sx={userCardsMenuItemStyles}
              data-testid="user-cards-select-menu-item"
            >
              {cardType?.toUpperCase() === 'VISA' || cardType?.toLowerCase() === 'visa' ? (
                <VisaIcon data-testid="visa-icon" />
              ) : cardType?.toUpperCase() === 'MASTERCARD' || cardType?.toLowerCase() === 'mastercard' || cardType?.toUpperCase() === 'MASTER' ? (
                <MasterCardIcon data-testid="mastercard-icon" />
              ) : (
                <CreditCardIcon data-testid="generic-card-icon" />
              )}
              {cardNumber}
            </MenuItem>
          ))}
          {onAddCard && (
            <MenuItem
              onClick={() => {
                handleClose();
                onAddCard();
              }}
              sx={userCardsMenuItemStyles}
              data-testid="user-cards-add-new-card-menu-item"
            >
              <CreditCardIcon />
              {addCardLabel}
            </MenuItem>
          )}
        </Menu>
      </Box>

      <Divider
        sx={userCardsDividerStyles}
        data-testid="user-cards-select-divider"
      />

      <SplitAmount
        pencilRef={selectCardPencilReference}
        title={title}
        primaryCard={primaryCard}
        secondaryCard={value}
        primaryAmount={amount?.primary ?? ''}
        secondaryAmount={amount?.secondary ?? ''}
        onChangeCard={() => handleClick('icon')}
      />
    </>
  );
};
